/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useReducer } from 'react'

import {
  ACTION,
  assign,
  DOT,
  DOT_PREV,
  DOT_NEXT,
  first,
  initial,
  last,
  next,
  prev,
  range,
  reducer,
  withPattern,
  PaginateValue,
} from './domain'
import type { InitialProps } from './domain'

export type UsePaginationProps = {
  max?: number
  min?: number
  groupLimit?: number
  currentPage?: number
  handleChange?: (curr: number) => void
  isJumper?: boolean
}

type UsePaginationReturnTypes = InitialProps & Actions

type Actions = {
  handleNext: () => void
  handleLast: () => void
  handlePrev: () => void
  handleFirst: () => void
  handleFixed: (num: number) => void
}

function usePagination(props: UsePaginationProps): UsePaginationReturnTypes {
  const {
    max = 1,
    min = 1,
    groupLimit = 4,
    currentPage = 1,
    handleChange = () => {},
    isJumper = false,
  } = props

  const init = initial({ current: currentPage })
  const [state, dispatch] = useReducer(reducer, init)

  const pattern = withPattern(groupLimit, state.current, min, max)
  // 1 ... [] ... max
  const middlePattern = pattern((group, current, min, max) => {
    return () =>
      current - group >= 0 &&
      current - group < max &&
      current + group - 1 <= max
  })
  // [] ... max
  const startPattern = pattern((group, current, min, max) => {
    return () => current - group < min && current + group <= max
  })
  // 1 ... []
  const endPattern = pattern((group, current, _min, max) => {
    return () => current - group <= max && current + group >= max
  })
  // 1 2 3
  const constantPattern = pattern((group, _current, _min, max) => {
    return () => max <= group + 1
  })

  const createCollection = (current: number): PaginateValue[] => {
    if (current < 0) {
      return []
    }
    const dotNext = isJumper ? DOT_NEXT : DOT
    const dotPrev = isJumper ? DOT_PREV : DOT
    if (constantPattern()) {
      return range(min, max)
    }

    if (middlePattern()) {
      const middleShow = 3
      const middleGroup =
        groupLimit - 1 < middleShow
          ? [current]
          : range(current - 1, current + 1)

      return [min, dotPrev, ...middleGroup, dotNext, max]
    }

    if (startPattern()) {
      const startGroup = range(min, groupLimit)
      return [...startGroup, dotNext, max]
    }

    if (endPattern()) {
      const endGroup = range(max - groupLimit + 1, max)
      return [min, dotPrev, ...endGroup]
    }

    return []
  }

  const update = (
    prevState: InitialProps,
    nextState: InitialProps,
  ): InitialProps => {
    const updateStatusWithNextState = {
      next: next(prevState.current, max),
      last: last(prevState.current, max),
      prev: prev(prevState.current, min),
      first: first(prevState.current, min),
      collection: createCollection(nextState.current),
      ...nextState,
      current: nextState.current < 0 ? 0 : nextState.current,
    }

    const update = assign(prevState, updateStatusWithNextState)

    return update
  }

  const handleUpdate = () => {
    dispatch({
      type: ACTION.UPDATE,
      payload: update(state, { current: currentPage }),
    })
  }

  const handleNext = () => {
    const nextState = {
      next: next(state.current, max),
      current: next(state.current, max) ? state.current + 1 : state.current,
    }

    handleChange(nextState.current)
    dispatch({ type: ACTION.UPDATE, payload: update(state, nextState) })
  }

  const handleLast = () => {
    const nextState = {
      last: last(state.current, max),
      current: max,
    }

    handleChange(nextState.current)
    dispatch({ type: ACTION.UPDATE, payload: update(state, nextState) })
  }

  const handlePrev = () => {
    const nextState = {
      prev: prev(state.current, min),
      current: prev(state.current, min) ? state.current - 1 : state.current,
    }

    handleChange(nextState.current)
    dispatch({ type: ACTION.UPDATE, payload: update(state, nextState) })
  }

  const handleFirst = () => {
    const nextState = {
      first: first(state.current, min),
      current: min,
    }

    handleChange(nextState.current)
    dispatch({ type: ACTION.UPDATE, payload: update(state, nextState) })
  }

  const handleFixed = (num: number) => {
    const nextState = {
      current: num > max ? max : num < min ? min : num,
      collection: createCollection(num),
    }

    handleChange(nextState.current)
    dispatch({ type: ACTION.UPDATE, payload: update(state, nextState) })
  }

  useEffect(() => {
    handleUpdate()
  }, [max, currentPage, groupLimit])

  const actions: Actions = {
    handleNext,
    handleLast,
    handlePrev,
    handleFirst,
    handleFixed,
  }

  return {
    ...state,
    ...actions,
  }
}

export default usePagination
